import { IsNotEmpty } from 'class-validator';
import { Mapper } from '../../../common/ddd/mapper.base';
import { IWorkingSessionsAnalyticsActivityEnum } from '../../../common/enum/working-sessions/working-sessions-analytics-activity.enum';
import { SelfWorkingSessionsEntity } from '../../../core/domain/entities/working-sessions/self-working-sessions.entity';
import { GetSelfWorkingSessionsResponseDto } from '../../../interface/dtos/working-sessions/get-self-working-sessions.response.dto';

export class SelfWorkingSessionsEntityPersistenceResponse {
  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.SessionId]: string;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart]: string;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.LocalSessionEnd]: string;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.LocalSessionStartWeek]: string;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.SessionDuration]: number;

  // TODO: Add validation on these fields when values are populated in the db.
  // Currently these values are null but instead of creating a new entity with zero values, return partial data
  [IWorkingSessionsAnalyticsActivityEnum.SessionCallDuration]: number;
  [IWorkingSessionsAnalyticsActivityEnum.SessionCallCount]: number;
  [IWorkingSessionsAnalyticsActivityEnum.SessionFocusDuration]: number;
  [IWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration]: number;
  [IWorkingSessionsAnalyticsActivityEnum.UpdatedAt]: string;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.SessionActivityDuration]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.TotalDuration]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.ActivityCount]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.ContextSwitches]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.FocusScore]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.DigitalIntensity]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.FocusPercentage]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.DayCount]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.CallDayCount]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.WeekCount]: number;

  @IsNotEmpty()
  [IWorkingSessionsAnalyticsActivityEnum.CallWeekCount]: number;
}

export class SelfWorkingSessionsEntityMapper
  implements
    Mapper<SelfWorkingSessionsEntity, GetSelfWorkingSessionsResponseDto>
{
  toDomain(
    record: SelfWorkingSessionsEntityPersistenceResponse
  ): SelfWorkingSessionsEntity {
    return SelfWorkingSessionsEntityMapper.toDomain(record);
  }

  static toDomain(
    record: SelfWorkingSessionsEntityPersistenceResponse
  ): SelfWorkingSessionsEntity {
    const entity = SelfWorkingSessionsEntity.create({
      sessionId: String(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionId]
      ),
      startTime: String(
        record[IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart]
      ),
      endTime: String(
        record[IWorkingSessionsAnalyticsActivityEnum.LocalSessionEnd]
      ),
      sessionStartWeek: String(
        record[IWorkingSessionsAnalyticsActivityEnum.LocalSessionStartWeek]
      ),
      duration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionDuration]
      ),
      sessionCallDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionCallDuration]
      ),
      callCount: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionCallCount]
      ),
      focusDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionFocusDuration]
      ),
      activityDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.SessionActivityDuration]
      ),
      totalDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.TotalDuration]
      ),
      totalSessionCallDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration]
      ),
      totalActivityDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration]
      ),
      totalFocusDuration: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration]
      ),
      activityCount: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.ActivityCount]
      ),
      contextSwitches: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.ContextSwitches]
      ),
      focusScore: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.FocusScore]
      ),
      digitalIntensity: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.DigitalIntensity]
      ),
      focusPercentage: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.FocusPercentage]
      ),
      dayCount: Number(record[IWorkingSessionsAnalyticsActivityEnum.DayCount]),
      callDayCount: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.CallDayCount]
      ),
      weekCount: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.WeekCount]
      ),
      callWeekCount: Number(
        record[IWorkingSessionsAnalyticsActivityEnum.CallWeekCount]
      ),
      updatedAt: record[IWorkingSessionsAnalyticsActivityEnum.UpdatedAt],
    });

    return entity;
  }

  toResponse(
    entity: SelfWorkingSessionsEntity
  ): GetSelfWorkingSessionsResponseDto {
    return SelfWorkingSessionsEntityMapper.toResponse(entity);
  }

  static toResponse(
    entity: SelfWorkingSessionsEntity
  ): GetSelfWorkingSessionsResponseDto {
    console.log('entity:', entity);
    return {
      sessionId: entity.sessionId,
      startTime: entity.startTime,
      endTime: entity.endTime,
      sessionStartWeek: entity.sessionStartWeek,
      duration: entity.duration,
      sessionCallDuration: entity.sessionCallDuration,
      callCount: entity.callCount,
      focusDuration: entity.focusDuration,
      activityDuration: entity.activityDuration,
      totalDuration: entity.totalDuration,
      totalSessionCallDuration: entity.totalSessionCallDuration,
      totalActivityDuration: entity.totalActivityDuration,
      totalFocusDuration: entity.totalFocusDuration,
      activityCount: entity.activityCount,
      contextSwitches: entity.contextSwitches,
      focusScore: entity.focusScore,
      digitalIntensity: entity.digitalIntensity,
      focusPercentage: entity.focusPercentage,
      dayCount: entity.dayCount,
      callDayCount: entity.callDayCount,
      weekCount: entity.weekCount,
      callWeekCount: entity.callWeekCount,
      updatedAt: entity.updatedAt.toISOString(),
    };
  }
}
